// Discover — Suggested for you as a Home-like pastel bento: compact cards, mixed sizes,
// Higgsfield atmosphere art (nature / aura / dream / gradients).

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DISCOVER_MOODS,
  fetchDailyWisdom,
  fetchDiscover,
  fetchLibrary,
  removeQuote,
  saveQuote,
  type DailyWisdom,
  type Quote,
} from '../api/wisdom';
import {
  WRITE_CATEGORIES,
  fetchJournalCatalog,
  fetchJournalSuggestionsToday,
  type JournalSuggestion,
  type SuggestionCategory,
} from '../api/suggestions';
import { FALLBACK_CATALOG, fallbackTodaySuggestions } from '../api/suggestion-fallback';
import {
  DiscoverCardArt,
  artForSuggestion,
  isHeroSuggestion,
} from '../components/DiscoverCardArt';
import { EmptyState, Spinner } from '../components/ui';
import { IconArrow, IconCheck, IconLock, IconSpark, IconTrash } from '../components/Icons';

type Tab = 'today' | 'write' | 'discover' | 'library';

export function DiscoverScreen() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('today');
  const [daily, setDaily] = useState<DailyWisdom | null>(null);
  const [suggestions, setSuggestions] = useState<JournalSuggestion[] | null>(null);
  const [catalog, setCatalog] = useState<JournalSuggestion[] | null>(null);
  const [writeFilter, setWriteFilter] = useState<SuggestionCategory | 'all'>('all');
  const [mood, setMood] = useState<string>(DISCOVER_MOODS[0]);
  const [discovered, setDiscovered] = useState<Quote[] | null>(null);
  const [library, setLibrary] = useState<Quote[] | null>(null);
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetchDailyWisdom(controller.signal)
      .then(setDaily)
      .catch(() => setDaily({ quote: null, reason: '', updatesInSeconds: 0 }));
    fetchJournalSuggestionsToday(controller.signal)
      .then((res) =>
        setSuggestions(res.suggestions.length > 0 ? res.suggestions : fallbackTodaySuggestions()),
      )
      .catch(() => setSuggestions(fallbackTodaySuggestions()));
    fetchJournalCatalog(controller.signal)
      .then((rows) => setCatalog(rows.length > 0 ? rows : FALLBACK_CATALOG))
      .catch(() => setCatalog(FALLBACK_CATALOG));
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (tab !== 'discover') return;
    const controller = new AbortController();
    setDiscovered(null);
    fetchDiscover(mood, controller.signal)
      .then(setDiscovered)
      .catch(() => setDiscovered([]));
    return () => controller.abort();
  }, [tab, mood]);

  useEffect(() => {
    if (tab !== 'write' || catalog !== null) return;
    const controller = new AbortController();
    fetchJournalCatalog(controller.signal)
      .then((rows) => setCatalog(rows.length > 0 ? rows : FALLBACK_CATALOG))
      .catch(() => setCatalog(FALLBACK_CATALOG));
    return () => controller.abort();
  }, [tab, catalog]);

  const loadLibrary = useCallback(() => {
    fetchLibrary()
      .then((result) => setLibrary(result.quotes))
      .catch(() => setLibrary([]));
  }, []);

  useEffect(() => {
    if (tab === 'library' && library === null) loadLibrary();
  }, [tab, library, loadLibrary]);

  function openSuggestion(card: JournalSuggestion) {
    if (card.route === 'chat') {
      navigate('/oreia', {
        state: { prompt: card.chatPrompt || `${card.title}. ${card.subtitle}` },
      });
      return;
    }
    navigate('/write', {
      state: {
        from: '/discover',
        seed: {
          title: card.seedTitle,
          body: card.seedBody,
          kind: card.category === 'dream' || card.id.startsWith('dream:') ? 'dream' : 'waking',
        },
      },
    });
  }

  async function onSave(quote: Quote) {
    setSaving(quote.text);
    try {
      await saveQuote(quote);
      setLibrary(null);
    } finally {
      setSaving(null);
    }
  }

  async function onRemove(id: string) {
    setLibrary((current) => current?.filter((q) => q.id !== id) ?? null);
    try {
      await removeQuote(id);
    } catch {
      loadLibrary();
    }
  }

  const filteredCatalog =
    catalog == null
      ? null
      : writeFilter === 'all'
        ? catalog.filter((c) => c.category !== 'contextual')
        : catalog.filter((c) => c.category === writeFilter);

  return (
    <>
      <div className="ohead">
        <div className="obrand">
          <span className="nm" style={{ fontSize: 22 }}>Discover</span>
        </div>
        <div className="olock">
          <IconLock /> Private
        </div>
      </div>

      <div className="ofilters" role="tablist" aria-label="Discover sections">
        {([
          ['today', 'Suggested for you'],
          ['write', 'Write'],
          ['discover', 'Browse'],
          ['library', 'Saved'],
        ] as const).map(([value, label]) => (
          <button
            key={value}
            role="tab"
            aria-selected={tab === value}
            className={`ochip${tab === value ? ' active' : ''}`}
            onClick={() => setTab(value)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'today' ? (
        daily === null || suggestions === null ? (
          <Spinner label="Choosing today's suggestions…" />
        ) : (
          <div className="obento odiscover-bento">
            {daily.quote ? (
              <div className="ocard ocard-bright span2 odiscover-quote">
                <span className="olabel">Today's line</span>
                <blockquote className="oserif odiscover-quote-text">
                  {daily.quote.text}
                </blockquote>
                <div className="orow" style={{ gap: 8, marginTop: 14, justifyContent: 'space-between' }}>
                  <span className="omono" style={{ fontSize: 11 }}>
                    {daily.quote.author ?? 'Unknown'}
                  </span>
                  <button
                    className="ochip"
                    onClick={() => onSave(daily.quote!)}
                    disabled={saving === daily.quote.text}
                  >
                    {saving === daily.quote.text ? (
                      <IconCheck style={{ width: 14, height: 14 }} />
                    ) : (
                      <IconSpark style={{ width: 14, height: 14 }} />
                    )}
                    {saving === daily.quote.text ? 'Saved' : 'Save'}
                  </button>
                </div>
                {daily.reason ? (
                  <p className="otext-mute odiscover-reason">{daily.reason}</p>
                ) : null}
              </div>
            ) : null}

            {suggestions.map((card, index) => (
              <SuggestionCard
                key={card.id}
                card={card}
                hero={isHeroSuggestion(card.id, index, suggestions.length)}
                onOpen={() => openSuggestion(card)}
              />
            ))}

            <button
              type="button"
              className="ocard ocard-light ocard-link odiscover-more"
              onClick={() => setTab('write')}
            >
              <DiscoverCardArt art="mint" />
              <span className="olabel">Practices</span>
              <h3 className="otitle" style={{ fontSize: 22, marginTop: 10 }}>
                Browse all
              </h3>
              <span className="orow" style={{ marginTop: 14, gap: 6 }}>
                <span className="omono" style={{ fontSize: 11 }}>Write tab</span>
                <span className="ocirc">
                  <IconArrow />
                </span>
              </span>
            </button>
          </div>
        )
      ) : null}

      {tab === 'write' ? (
        <>
          <div className="ofilters" style={{ marginTop: 4 }} aria-label="Practice categories">
            {WRITE_CATEGORIES.map((value) => (
              <button
                key={value.id}
                className={`ochip${writeFilter === value.id ? ' active' : ''}`}
                onClick={() => setWriteFilter(value.id)}
              >
                {value.label}
              </button>
            ))}
          </div>

          {filteredCatalog === null ? (
            <Spinner label="Opening practices…" />
          ) : filteredCatalog.length === 0 ? (
            <EmptyState title="Nothing in this category." body="Try another filter." />
          ) : (
            <div className="obento odiscover-bento">
              {filteredCatalog.map((card, index) => (
                <SuggestionCard
                  key={card.id}
                  card={card}
                  hero={index % 5 === 0}
                  onOpen={() => openSuggestion(card)}
                />
              ))}
            </div>
          )}
        </>
      ) : null}

      {tab === 'discover' ? (
        <>
          <div className="ofilters" style={{ marginTop: 4 }} aria-label="Moods">
            {DISCOVER_MOODS.map((value) => (
              <button
                key={value}
                className={`ochip${mood === value ? ' active' : ''}`}
                onClick={() => setMood(value)}
              >
                {value[0].toUpperCase() + value.slice(1)}
              </button>
            ))}
          </div>

          {discovered === null ? (
            <Spinner label="Reading…" />
          ) : discovered.length === 0 ? (
            <EmptyState title="Nothing here right now." body="Try another mood." />
          ) : (
            <div className="ostack" style={{ gap: 12 }}>
              {discovered.map((quote, i) => (
                <QuoteCard
                  key={`${quote.text}-${i}`}
                  quote={quote}
                  onSave={() => onSave(quote)}
                  saving={saving === quote.text}
                />
              ))}
            </div>
          )}
        </>
      ) : null}

      {tab === 'library' ? (
        library === null ? (
          <Spinner label="Opening your library…" />
        ) : library.length === 0 ? (
          <EmptyState
            title="Nothing saved yet."
            body="Save a line from Suggested for you or Browse and it will wait for you here."
          />
        ) : (
          <div className="ostack" style={{ gap: 12 }}>
            {library.map((quote) => (
              <QuoteCard
                key={quote.id}
                quote={quote}
                onRemove={quote.id ? () => onRemove(quote.id!) : undefined}
              />
            ))}
          </div>
        )
      ) : null}

      <div style={{ height: 60 }} />
    </>
  );
}

function categoryLabel(card: JournalSuggestion): string {
  if (card.source === 'pattern') return 'Pattern';
  if (card.source === 'dream_theme') return 'Dream';
  switch (card.category) {
    case 'practice':
      return 'Practice';
    case 'desire':
      return 'Desire';
    case 'inner':
      return 'Inner';
    case 'dream':
      return 'Dream';
    case 'life':
      return 'Life';
    default:
      return 'Write';
  }
}

function SuggestionCard({
  card,
  hero,
  onOpen,
}: {
  card: JournalSuggestion;
  hero: boolean;
  onOpen(): void;
}) {
  const art = artForSuggestion(card.id, card.category);
  const cta = card.route === 'chat' ? 'Talk it through' : 'Write this';

  return (
    <button
      type="button"
      className={`ocard ocard-light ocard-link odiscover-card${hero ? ' span2 odiscover-card-hero' : ''}`}
      onClick={onOpen}
    >
      <DiscoverCardArt art={art} />
      <div className="ohead-row" style={{ marginBottom: hero ? 12 : 8 }}>
        <span className="otag">
          <span className="dot" />
          {categoryLabel(card)}
        </span>
      </div>
      <h3 className={`otitle odiscover-card-title${hero ? '' : ' compact'}`}>{card.title}</h3>
      {hero ? (
        <p className="obody odiscover-card-body">{card.subtitle}</p>
      ) : null}
      {hero && card.reason ? (
        <p className="otext-mute odiscover-card-why">{card.reason}</p>
      ) : null}
      <div className="orow odiscover-card-cta">
        <span className="omono" style={{ fontSize: 11 }}>
          {cta}
        </span>
        <span className="ocirc">
          <IconArrow />
        </span>
      </div>
    </button>
  );
}

function QuoteCard({
  quote,
  featured,
  onSave,
  onRemove,
  saving,
}: {
  quote: Quote;
  featured?: boolean;
  onSave?(): void;
  onRemove?(): void;
  saving?: boolean;
}) {
  return (
    <figure className={`ocard ${featured ? 'ocard-bright' : 'ocard-light'}`} style={{ margin: 0 }}>
      <blockquote
        className="oserif"
        style={{
          margin: 0,
          fontSize: featured ? 27 : 20,
          lineHeight: 1.35,
          letterSpacing: '-0.01em',
        }}
      >
        {quote.text}
      </blockquote>

      {quote.emphasis ? (
        <p className="oitalic oserif" style={{ fontSize: 16, margin: '14px 0 0', color: 'var(--ink-soft)' }}>
          {quote.emphasis}
        </p>
      ) : null}

      <figcaption className="ometa" style={{ marginTop: 16 }}>
        <span className="omono">
          {quote.author ?? 'Unknown'}
          {quote.source ? ` · ${quote.source}` : ''}
        </span>
      </figcaption>

      {onSave || onRemove ? (
        <div className="orow" style={{ gap: 8, marginTop: 16 }}>
          {onSave ? (
            <button className="ochip" onClick={onSave} disabled={saving}>
              {saving ? <IconCheck style={{ width: 14, height: 14 }} /> : <IconSpark style={{ width: 14, height: 14 }} />}
              {saving ? 'Saved' : 'Save'}
            </button>
          ) : null}
          {onRemove ? (
            <button className="ochip" onClick={onRemove}>
              <IconTrash style={{ width: 14, height: 14 }} /> Remove
            </button>
          ) : null}
        </div>
      ) : null}
    </figure>
  );
}
